import React, { useState } from 'react';
import { useEditorContext } from '../context/EditorContext';
import { useAssetContext } from '../context/AssetContext';
import type { AnimationPreset, TemplateLayer, IdleAnimation, ImageLayer as ImageLayerType } from '../types';

// ─── Shared small UI atoms ────────────────────────────────────

function Label({ children }: { children: React.ReactNode }) {
  return (
    <label style={{ fontSize: 10, color: '#94a3b8', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
      {children}
    </label>
  );
}

function NumInput({
  label,
  value,
  onChange,
  step = 1,
  min,
  max,
  unit,
}: {
  label: string;
  value: number;
  onChange: (v: number) => void;
  step?: number;
  min?: number;
  max?: number;
  unit?: string;
}) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <Label>{label}</Label>
      <div style={{ display: 'flex', alignItems: 'center', background: '#1e293b', border: '1px solid #334155', borderRadius: 5, overflow: 'hidden' }}>
        <input
          type="number"
          value={Math.round(value * 10) / 10}
          step={step}
          min={min}
          max={max}
          onChange={(e) => onChange(parseFloat(e.target.value) || 0)}
          style={{
            flex: 1, padding: '4px 6px', background: 'transparent', border: 'none',
            color: '#e2e8f0', fontSize: 12, outline: 'none', width: 0,
          }}
        />
        {unit && <span style={{ paddingRight: 6, fontSize: 10, color: '#64748b' }}>{unit}</span>}
      </div>
    </div>
  );
}

function ColorInput({ label, value, onChange }: { label: string; value: string; onChange: (v: string) => void }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <Label>{label}</Label>
      <div style={{ display: 'flex', gap: 6, alignItems: 'center', background: '#1e293b', border: '1px solid #334155', borderRadius: 5, padding: '3px 6px' }}>
        <input type="color" value={value.startsWith('#') ? value : '#ffffff'} onChange={(e) => onChange(e.target.value)}
          style={{ width: 22, height: 22, border: 'none', background: 'none', cursor: 'pointer', padding: 0 }} />
        <input type="text" value={value} onChange={(e) => onChange(e.target.value)}
          style={{ flex: 1, background: 'transparent', border: 'none', color: '#e2e8f0', fontSize: 11, fontFamily: 'monospace', outline: 'none' }} />
      </div>
    </div>
  );
}

function SelectInput<T extends string>({
  label, value, options, onChange,
}: { label: string; value: T; options: { value: T; label: string }[]; onChange: (v: T) => void }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <Label>{label}</Label>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value as T)}
        style={{
          background: '#1e293b', border: '1px solid #334155', borderRadius: 5,
          color: '#e2e8f0', fontSize: 12, padding: '4px 8px', outline: 'none',
        }}
      >
        {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    </div>
  );
}

function Row({ children }: { children: React.ReactNode }) {
  return <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>{children}</div>;
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      <div style={{ fontSize: 10, color: '#475569', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', borderBottom: '1px solid #1e293b', paddingBottom: 4 }}>
        {title}
      </div>
      {children}
    </div>
  );
}

// ─── Tab definitions ──────────────────────────────────────────

type PanelTab = 'position' | 'appearance' | 'text' | 'animation';

const TABS: { id: PanelTab; label: string }[] = [
  { id: 'position', label: 'Position' },
  { id: 'appearance', label: 'Appear' },
  { id: 'text', label: 'Text' },
  { id: 'animation', label: 'Animate' },
];

const ANIMATION_PRESETS: { value: AnimationPreset; label: string }[] = [
  { value: 'none', label: 'None' },
  { value: 'fade', label: 'Fade' },
  { value: 'slide-left', label: 'Slide Left' },
  { value: 'slide-right', label: 'Slide Right' },
  { value: 'slide-up', label: 'Slide Up' },
  { value: 'slide-down', label: 'Slide Down' },
  { value: 'zoom-in', label: 'Zoom In' },
  { value: 'zoom-out', label: 'Zoom Out' },
  { value: 'scale', label: 'Scale' },
  { value: 'bounce', label: 'Bounce' },
  { value: 'blur', label: 'Blur' },
  { value: 'rotate', label: 'Rotate' },
  { value: 'elastic', label: 'Elastic' },
  { value: 'wipe-left', label: 'Wipe Left' },
  { value: 'wipe-right', label: 'Wipe Right' },
];

// ─── Tab panels ───────────────────────────────────────────────

function PositionTab({ layer }: { layer: TemplateLayer }) {
  const { updateLayerPosition } = useEditorContext();
  const { position } = layer;
  const upd = (patch: Partial<typeof position>) => updateLayerPosition(layer.id, patch);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <Section title="Location">
        <Row>
          <NumInput label="X" value={position.x} onChange={(v) => upd({ x: v })} unit="px" />
          <NumInput label="Y" value={position.y} onChange={(v) => upd({ y: v })} unit="px" />
        </Row>
      </Section>
      <Section title="Size">
        <Row>
          <NumInput label="Width" value={position.width} onChange={(v) => upd({ width: Math.max(1, v) })} unit="px" min={1} />
          <NumInput label="Height" value={position.height} onChange={(v) => upd({ height: Math.max(1, v) })} unit="px" min={1} />
        </Row>
      </Section>
      <Section title="Transform">
        <Row>
          <NumInput label="Rotation" value={position.rotation ?? 0} onChange={(v) => upd({ rotation: v })} unit="°" step={0.5} />
          <NumInput label="Z-Index" value={position.zIndex} onChange={(v) => upd({ zIndex: Math.round(v) })} min={0} />
        </Row>
      </Section>
    </div>
  );
}

function ToggleRow({ label, checked, onChange }: { label: string; checked: boolean; onChange: (v: boolean) => void }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
      <Label>{label}</Label>
      <button
        type="button"
        onClick={() => onChange(!checked)}
        style={{
          width: 44, height: 22, borderRadius: 11, border: 'none', cursor: 'pointer', padding: 2,
          background: checked ? '#ef4444' : '#334155',
          position: 'relative', transition: 'background 0.2s',
          display: 'flex', alignItems: 'center',
        }}
      >
        <span style={{
          width: 18, height: 18, borderRadius: '50%', background: '#fff', display: 'block',
          transition: 'transform 0.2s',
          transform: checked ? 'translateX(22px)' : 'translateX(0)',
        }} />
      </button>
    </div>
  );
}

function AppearanceTab({ layer }: { layer: TemplateLayer }) {
  const { updateLayer, updateDuration, state: editorState } = useEditorContext();
  const { state: assetState } = useAssetContext();
  const upd = (patch: Record<string, unknown>) => updateLayer(layer.id, patch);

  const shadow = layer.shadow ?? { x: 0, y: 2, blur: 8, color: '#00000066' };
  const border = layer.border ?? { width: 0, color: '#ffffff', style: 'solid' as const };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      {/* Anchor-only: object fit + mute */}
      {layer.type === 'anchor' && (
        <>
          <Section title="Video Fit">
            <SelectInput<'cover' | 'contain' | 'fill'>
              label="Object Fit"
              value={(layer as import('../types').AnchorLayer).objectFit ?? 'cover'}
              options={[
                { value: 'cover',   label: 'Cover (fill, may crop)' },
                { value: 'contain', label: 'Contain (letterbox, no crop)' },
                { value: 'fill',    label: 'Fill (stretch)' },
              ]}
              onChange={(v) => upd({ objectFit: v })}
            />
          </Section>
          <Section title="Audio">
            <ToggleRow
              label="Mute Video Audio"
              checked={(layer as import('../types').AnchorLayer).muted ?? false}
              onChange={(v) => upd({ muted: v })}
            />
            <div style={{ fontSize: 10, color: '#475569', lineHeight: 1.4 }}>
              Mute the anchor video's native audio when using a separate voiceover.
            </div>
          </Section>
        </>
      )}

      {/* Audio-only: volume + playback speed */}
      {layer.type === 'audio' && (
        <Section title="Audio">
          <ToggleRow
            label="Mute"
            checked={(layer as import('../types').AudioLayer).muted ?? false}
            onChange={(v) => upd({ muted: v })}
          />
          <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <Label>Volume</Label>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <input
                type="range" min={0} max={1} step={0.01}
                value={(layer as import('../types').AudioLayer).volume ?? 1}
                onChange={(e) => upd({ volume: parseFloat(e.target.value) })}
                style={{ flex: 1, accentColor: '#06b6d4' }}
              />
              <span style={{ fontSize: 11, color: '#94a3b8', width: 36, textAlign: 'right' }}>
                {Math.round(((layer as import('../types').AudioLayer).volume ?? 1) * 100)}%
              </span>
            </div>
          </div>
          {/* Speed — only for voice over (not background music, which loops) */}
          {(layer as import('../types').AudioLayer).assetKey === 'voiceAudio' && (() => {
            const audioLayer = layer as import('../types').AudioLayer;
            const currentRate = audioLayer.playbackRate ?? 1;
            // Duration of the uploaded voice over file (seconds)
            const asset = assetState.assets[audioLayer.assetKey];
            const rawDuration = asset?.duration ?? null;
            // Effective duration after speed adjustment
            const effectiveDuration = rawDuration !== null
              ? Math.round((rawDuration / currentRate) * 10) / 10
              : null;
            // Current composition duration in seconds (stored directly in metadata)
            const videoDuration = editorState.editableConfig?.metadata.duration ?? 0;

            const fmt = (s: number) => {
              const m = Math.floor(s / 60);
              const sec = (s % 60).toFixed(1);
              return m > 0 ? `${m}m ${sec}s` : `${sec}s`;
            };

            return (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                <Label>Playback Speed</Label>
                <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                  {[0.5, 0.75, 1, 1.25, 1.5, 2].map((rate) => {
                    const active = Math.abs(currentRate - rate) < 0.01;
                    return (
                      <button
                        key={rate}
                        type="button"
                        onClick={() => upd({ playbackRate: rate })}
                        style={{
                          padding: '3px 8px', borderRadius: 4, border: 'none', cursor: 'pointer', fontSize: 10, fontWeight: 600,
                          background: active ? '#1d4ed8' : '#1e293b',
                          color: active ? '#fff' : '#64748b',
                          outline: active ? '1px solid #3b82f6' : '1px solid #334155',
                        }}
                      >
                        {rate}×
                      </button>
                    );
                  })}
                </div>

                {/* Duration info panel */}
                {rawDuration !== null && (
                  <div style={{
                    padding: '6px 8px', borderRadius: 4, background: '#0f172a',
                    border: '1px solid #1e293b', display: 'flex', flexDirection: 'column', gap: 4,
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10 }}>
                      <span style={{ color: '#64748b' }}>Original audio</span>
                      <span style={{ color: '#94a3b8', fontWeight: 600 }}>{fmt(rawDuration)}</span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10 }}>
                      <span style={{ color: '#64748b' }}>At {currentRate}× speed</span>
                      <span style={{
                        fontWeight: 700,
                        color: effectiveDuration! > videoDuration ? '#f87171' : '#4ade80',
                      }}>
                        {fmt(effectiveDuration!)}
                      </span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10 }}>
                      <span style={{ color: '#64748b' }}>Video duration</span>
                      <span style={{ color: '#94a3b8' }}>{fmt(videoDuration)}</span>
                    </div>

                    {/* Mismatch warning + apply button */}
                    {Math.abs(effectiveDuration! - videoDuration) > 0.5 && (
                      <div style={{ marginTop: 2, padding: '5px 6px', borderRadius: 3, background: '#78350f33', border: '1px solid #92400e66' }}>
                        <div style={{ fontSize: 9, color: '#fbbf24', lineHeight: 1.5 }}>
                          {effectiveDuration! < videoDuration
                            ? `⚠️ Audio ends ${fmt(videoDuration - effectiveDuration!)} early — video will have silence.`
                            : `⚠️ Audio is ${fmt(effectiveDuration! - videoDuration)} longer than video — it will be cut off.`
                          }
                        </div>
                        <button
                          type="button"
                          onClick={() => updateDuration(effectiveDuration!)}
                          style={{
                            marginTop: 5, width: '100%', padding: '4px 0', borderRadius: 3,
                            border: 'none', cursor: 'pointer', fontSize: 10, fontWeight: 700,
                            background: '#1d4ed8', color: '#fff',
                          }}
                        >
                          Set video duration to {fmt(effectiveDuration!)}
                        </button>
                      </div>
                    )}
                  </div>
                )}

                {/* Preview note */}
                {currentRate !== 1 && (
                  <div style={{ fontSize: 9, color: '#475569', lineHeight: 1.4 }}>
                    ⚡ Speed applies to final render only — preview plays at 1×.
                  </div>
                )}
              </div>
            );
          })()}
        </Section>
      )}

      {/* Image-only: idle / continuous animation */}
      {layer.type === 'image' && (
        <Section title="Motion Effect (While Visible)">
          <SelectInput<IdleAnimation>
            label="Effect"
            value={(layer as ImageLayerType).idleAnimation ?? 'none'}
            options={[
              { value: 'none',         label: 'None' },
              { value: 'zoom-in',      label: 'Slow Zoom In' },
              { value: 'zoom-out',     label: 'Slow Zoom Out' },
              { value: 'pan-left',     label: 'Pan Left' },
              { value: 'pan-right',    label: 'Pan Right' },
              { value: 'ken-burns-1',  label: 'Ken Burns 1 (Zoom In + Right)' },
              { value: 'ken-burns-2',  label: 'Ken Burns 2 (Zoom Out + Left)' },
              { value: 'ken-burns-3',  label: 'Ken Burns 3 (Zoom In + Left)' },
              { value: 'ken-burns-4',  label: 'Ken Burns 4 (Zoom Out + Right)' },
            ]}
            onChange={(v) => upd({ idleAnimation: v })}
          />
          {((layer as ImageLayerType).idleAnimation ?? 'none') !== 'none' && (
            <div>
              <Label>Speed / Intensity</Label>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <input
                  type="range" min={0} max={1} step={0.05}
                  value={(layer as ImageLayerType).idleSpeed ?? 0.5}
                  onChange={(e) => upd({ idleSpeed: parseFloat(e.target.value) })}
                  style={{ flex: 1, accentColor: '#22d3ee' }}
                />
                <span style={{ fontSize: 11, color: '#94a3b8', width: 28, textAlign: 'right' }}>
                  {Math.round(((layer as ImageLayerType).idleSpeed ?? 0.5) * 100)}%
                </span>
              </div>
            </div>
          )}
        </Section>
      )}

      {/* ── Layer-type colour controls ─────────────────────────── */}

      {layer.type === 'top-bar' && (
        <Section title="Colors">
          <ColorInput
            label="Background Color"
            value={(layer as import('../types').TopBarLayer).backgroundColor}
            onChange={(v) => upd({ backgroundColor: v })}
          />
        </Section>
      )}

      {layer.type === 'ticker' && (
        <Section title="Colors">
          <ColorInput
            label="Background Color"
            value={(layer as import('../types').TickerLayer).backgroundColor}
            onChange={(v) => upd({ backgroundColor: v })}
          />
          <ColorInput
            label="Text Color"
            value={(layer as import('../types').TickerLayer).font?.color ?? '#ffffff'}
            onChange={(v) => upd({ font: { ...(layer as import('../types').TickerLayer).font, color: v } })}
          />
        </Section>
      )}

      {layer.type === 'lower-third' && (
        <Section title="Colors">
          <ColorInput
            label="Background Color"
            value={(layer as import('../types').LowerThirdLayer).backgroundColor}
            onChange={(v) => upd({ backgroundColor: v })}
          />
          <ColorInput
            label="Accent Color"
            value={(layer as import('../types').LowerThirdLayer).accentColor ?? '#3b82f6'}
            onChange={(v) => upd({ accentColor: v })}
          />
        </Section>
      )}

      {layer.type === 'breaking-news-banner' && (
        <Section title="Colors">
          <ColorInput
            label="Bar Background"
            value={(layer as import('../types').BreakingNewsBannerLayer).backgroundColor}
            onChange={(v) => upd({ backgroundColor: v })}
          />
          <ColorInput
            label="Label Background"
            value={(layer as import('../types').BreakingNewsBannerLayer).labelBackgroundColor ?? '#dc2626'}
            onChange={(v) => upd({ labelBackgroundColor: v })}
          />
        </Section>
      )}

      <Section title="Opacity">
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <input type="range" min={0} max={1} step={0.01} value={layer.opacity}
            onChange={(e) => upd({ opacity: parseFloat(e.target.value) })}
            style={{ flex: 1, accentColor: '#3b82f6' }} />
          <span style={{ fontSize: 11, color: '#94a3b8', width: 36, textAlign: 'right' }}>
            {Math.round(layer.opacity * 100)}%
          </span>
        </div>
      </Section>

      <Section title="Shape">
        <NumInput label="Border Radius" value={layer.borderRadius ?? 0} onChange={(v) => upd({ borderRadius: v })} unit="px" min={0} />
      </Section>

      <Section title="Border">
        <Row>
          <NumInput label="Width" value={border.width} onChange={(v) => upd({ border: { ...border, width: v } })} unit="px" min={0} />
          <SelectInput<'solid' | 'dashed' | 'dotted'>
            label="Style"
            value={border.style}
            options={[{ value: 'solid', label: 'Solid' }, { value: 'dashed', label: 'Dashed' }, { value: 'dotted', label: 'Dotted' }]}
            onChange={(v) => upd({ border: { ...border, style: v } })}
          />
        </Row>
        <ColorInput label="Color" value={border.color} onChange={(v) => upd({ border: { ...border, color: v } })} />
      </Section>

      <Section title="Shadow">
        <Row>
          <NumInput label="X" value={shadow.x} onChange={(v) => upd({ shadow: { ...shadow, x: v } })} unit="px" />
          <NumInput label="Y" value={shadow.y} onChange={(v) => upd({ shadow: { ...shadow, y: v } })} unit="px" />
        </Row>
        <NumInput label="Blur" value={shadow.blur} onChange={(v) => upd({ shadow: { ...shadow, blur: Math.max(0, v) } })} unit="px" min={0} />
        <ColorInput label="Color" value={shadow.color} onChange={(v) => upd({ shadow: { ...shadow, color: v } })} />
      </Section>
    </div>
  );
}

function TextTab({ layer }: { layer: TemplateLayer }) {
  const { updateLayer } = useEditorContext();

  // Text properties live in different places depending on layer type
  // We look for a 'font' or 'reporterNameFont' field
  const getFont = () => {
    if ('font' in layer && layer.font && typeof layer.font === 'object') return layer.font as unknown as Record<string, unknown>;
    if ('reporterNameFont' in layer && layer.reporterNameFont) return layer.reporterNameFont as unknown as Record<string, unknown>;
    return null;
  };

  const font = getFont();

  if (!font) {
    return (
      <div style={{ padding: '20px 0', textAlign: 'center', color: '#475569', fontSize: 12 }}>
        This layer type has no text properties
      </div>
    );
  }

  const setFont = (patch: Record<string, unknown>) => {
    if ('font' in layer) {
      updateLayer(layer.id, { font: { ...font, ...patch } });
    } else if ('reporterNameFont' in layer) {
      updateLayer(layer.id, { reporterNameFont: { ...font, ...patch } });
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <Section title="Typography">
        <NumInput label="Font Size" value={Number(font.fontSize ?? 24)} onChange={(v) => setFont({ fontSize: v })} unit="px" min={4} />
        <SelectInput<string>
          label="Weight"
          value={String(font.fontWeight ?? 400)}
          options={[
            { value: '400', label: 'Regular (400)' },
            { value: '500', label: 'Medium (500)' },
            { value: '600', label: 'SemiBold (600)' },
            { value: '700', label: 'Bold (700)' },
            { value: '800', label: 'ExtraBold (800)' },
            { value: '900', label: 'Black (900)' },
          ]}
          onChange={(v) => setFont({ fontWeight: parseInt(v) })}
        />
        <SelectInput<string>
          label="Transform"
          value={String(font.textTransform ?? 'none')}
          options={[
            { value: 'none', label: 'None' },
            { value: 'uppercase', label: 'UPPERCASE' },
            { value: 'lowercase', label: 'lowercase' },
            { value: 'capitalize', label: 'Capitalize' },
          ]}
          onChange={(v) => setFont({ textTransform: v })}
        />
      </Section>
      <Section title="Spacing">
        <Row>
          <NumInput label="Letter Spacing" value={Number(font.letterSpacing ?? 0)} onChange={(v) => setFont({ letterSpacing: v })} step={0.1} />
          <NumInput label="Line Height" value={Number(font.lineHeight ?? 1.2)} onChange={(v) => setFont({ lineHeight: v })} step={0.05} min={0.5} />
        </Row>
      </Section>
      <Section title="Color">
        <ColorInput label="Text Color" value={String(font.color ?? '#ffffff')} onChange={(v) => setFont({ color: v })} />
      </Section>
    </div>
  );
}

function AnimationTab({ layer }: { layer: TemplateLayer }) {
  const { updateLayer } = useEditorContext();
  const { animation } = layer;
  const upd = (patch: Partial<typeof animation>) => updateLayer(layer.id, { animation: { ...animation, ...patch } });

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <Section title="Enter">
        <SelectInput<AnimationPreset>
          label="Preset"
          value={animation.enter}
          options={ANIMATION_PRESETS}
          onChange={(v) => upd({ enter: v })}
        />
        <Row>
          <NumInput label="Duration" value={animation.enterDuration} onChange={(v) => upd({ enterDuration: Math.max(0, Math.round(v)) })} unit="fr" min={0} />
          <NumInput label="Delay" value={animation.enterDelay} onChange={(v) => upd({ enterDelay: Math.max(0, Math.round(v)) })} unit="fr" min={0} />
        </Row>
      </Section>

      <Section title="Exit">
        <SelectInput<AnimationPreset>
          label="Preset"
          value={animation.exit}
          options={ANIMATION_PRESETS}
          onChange={(v) => upd({ exit: v })}
        />
        <Row>
          <NumInput label="Duration" value={animation.exitDuration} onChange={(v) => upd({ exitDuration: Math.max(0, Math.round(v)) })} unit="fr" min={0} />
          <NumInput label="Exit At" value={animation.exitAt} onChange={(v) => upd({ exitAt: Math.round(v) })} unit="fr" />
        </Row>
      </Section>

      <div style={{ fontSize: 10, color: '#475569', lineHeight: 1.5 }}>
        "fr" = frames. At 30fps, 30 fr = 1 second. exitAt -1 means end of clip.
      </div>
    </div>
  );
}

// ─── Text element (headline / subheadline) editor ────────────

function TextElementEditor() {
  const { selectedTextElement, updateLayerPosition, updateTextElement } = useEditorContext();
  const [tab, setTab] = useState<'position' | 'text'>('position');

  if (!selectedTextElement) return null;
  const { id, label, config } = selectedTextElement;
  const font = config.font as unknown as Record<string, unknown>;

  const updPos = (patch: Partial<typeof config.position>) =>
    updateLayerPosition(id, patch);

  const setFont = (patch: Record<string, unknown>) =>
    updateTextElement(id, { font: { ...font, ...patch } });

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <div style={{ padding: '10px 14px 8px', borderBottom: '1px solid #1e293b', flexShrink: 0 }}>
        <div style={{ fontSize: 11, fontWeight: 600, color: '#e2e8f0', marginBottom: 2 }}>{label}</div>
        <div style={{ fontSize: 10, color: '#64748b', fontFamily: 'monospace' }}>text element · {id}</div>
      </div>

      <div style={{ display: 'flex', borderBottom: '1px solid #1e293b', flexShrink: 0 }}>
        {(['position', 'text'] as const).map((t) => (
          <button key={t} type="button" onClick={() => setTab(t)}
            style={{
              flex: 1, padding: '7px 0', fontSize: 10, fontWeight: 600, border: 'none', cursor: 'pointer',
              background: 'transparent',
              color: tab === t ? '#3b82f6' : '#64748b',
              borderBottom: `2px solid ${tab === t ? '#3b82f6' : 'transparent'}`,
            }}>
            {t === 'position' ? 'Position' : 'Text'}
          </button>
        ))}
      </div>

      <div style={{ flex: 1, overflowY: 'auto', padding: 14, display: 'flex', flexDirection: 'column', gap: 14 }}>
        {tab === 'position' && (
          <>
            <Section title="Location">
              <Row>
                <NumInput label="X" value={config.position.x} onChange={(v) => updPos({ x: v })} unit="px" />
                <NumInput label="Y" value={config.position.y} onChange={(v) => updPos({ y: v })} unit="px" />
              </Row>
            </Section>
            <Section title="Size">
              <Row>
                <NumInput label="Width" value={config.position.width} onChange={(v) => updPos({ width: Math.max(1, v) })} unit="px" min={1} />
                <NumInput label="Height" value={config.position.height} onChange={(v) => updPos({ height: Math.max(1, v) })} unit="px" min={1} />
              </Row>
            </Section>
            <Section title="Max Lines">
              <NumInput label="Max Lines" value={config.maxLines} onChange={(v) => updateTextElement(id, { maxLines: Math.max(1, Math.round(v)) })} min={1} />
            </Section>
          </>
        )}

        {tab === 'text' && (
          <>
            <Section title="Typography">
              <NumInput label="Font Size" value={Number(font.fontSize ?? 48)} onChange={(v) => setFont({ fontSize: v })} unit="px" min={4} />
              <SelectInput<string>
                label="Weight"
                value={String(font.fontWeight ?? 700)}
                options={[
                  { value: '400', label: 'Regular' }, { value: '500', label: 'Medium' },
                  { value: '600', label: 'SemiBold' }, { value: '700', label: 'Bold' },
                  { value: '800', label: 'ExtraBold' }, { value: '900', label: 'Black' },
                ]}
                onChange={(v) => setFont({ fontWeight: parseInt(v) })}
              />
              <SelectInput<string>
                label="Transform"
                value={String(font.textTransform ?? 'none')}
                options={[
                  { value: 'none', label: 'None' }, { value: 'uppercase', label: 'UPPERCASE' },
                  { value: 'lowercase', label: 'lowercase' }, { value: 'capitalize', label: 'Capitalize' },
                ]}
                onChange={(v) => setFont({ textTransform: v })}
              />
              {/* Text alignment — left / center / right */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                <Label>Alignment</Label>
                <div style={{ display: 'flex', gap: 4 }}>
                  {(['left', 'center', 'right'] as const).map((align) => {
                    const active = String(font.textAlign ?? 'left') === align;
                    const labels: Record<string, string> = { left: '≡ Left', center: '≡ Center', right: '≡ Right' };
                    return (
                      <button
                        key={align}
                        type="button"
                        onClick={() => setFont({ textAlign: align })}
                        title={labels[align]}
                        style={{
                          flex: 1, padding: '5px 2px', borderRadius: 5, border: 'none', cursor: 'pointer',
                          background: active ? '#1d4ed8' : '#1e293b',
                          color: active ? '#fff' : '#64748b',
                          fontSize: 10, fontWeight: 600,
                          outline: active ? '1px solid #3b82f6' : '1px solid #334155',
                          transition: 'all 0.1s',
                        }}
                      >
                        {align === 'left' && (
                          <svg width="14" height="10" viewBox="0 0 14 10" fill="currentColor">
                            <rect x="0" y="0" width="14" height="2" rx="1"/>
                            <rect x="0" y="4" width="10" height="2" rx="1"/>
                            <rect x="0" y="8" width="12" height="2" rx="1"/>
                          </svg>
                        )}
                        {align === 'center' && (
                          <svg width="14" height="10" viewBox="0 0 14 10" fill="currentColor">
                            <rect x="0" y="0" width="14" height="2" rx="1"/>
                            <rect x="2" y="4" width="10" height="2" rx="1"/>
                            <rect x="1" y="8" width="12" height="2" rx="1"/>
                          </svg>
                        )}
                        {align === 'right' && (
                          <svg width="14" height="10" viewBox="0 0 14 10" fill="currentColor">
                            <rect x="0" y="0" width="14" height="2" rx="1"/>
                            <rect x="4" y="4" width="10" height="2" rx="1"/>
                            <rect x="2" y="8" width="12" height="2" rx="1"/>
                          </svg>
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>
            </Section>
            <Section title="Spacing">
              <Row>
                <NumInput label="Letter Spacing" value={Number(font.letterSpacing ?? 0)} onChange={(v) => setFont({ letterSpacing: v })} step={0.1} />
                <NumInput label="Line Height" value={Number(font.lineHeight ?? 1.2)} onChange={(v) => setFont({ lineHeight: v })} step={0.05} min={0.5} />
              </Row>
            </Section>
            <Section title="Color">
              <ColorInput label="Text Color" value={String(font.color ?? '#ffffff')} onChange={(v) => setFont({ color: v })} />
            </Section>
          </>
        )}
      </div>
    </div>
  );
}

// ─── Main component ───────────────────────────────────────────

export function PropertiesEditor() {
  const { selectedLayer, selectedTextElement } = useEditorContext();
  const [tab, setTab] = useState<PanelTab>('position');

  // Text element selected (headline / subheadline)
  if (selectedTextElement) {
    return <TextElementEditor />;
  }

  // No selection
  if (!selectedLayer) {
    return (
      <div style={{
        flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
        color: '#475569', fontSize: 12, gap: 8, padding: 24, textAlign: 'center',
      }}>
        <svg width="32" height="32" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24" style={{ color: '#334155' }}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M9 4.5v15m6-15v15M3.75 9h16.5M3.75 15h16.5" />
        </svg>
        <span>Click any layer or the<br /><strong style={{ color: '#fbbf24' }}>T Headline</strong> / <strong style={{ color: '#a3e635' }}>T Subheadline</strong><br />overlay on the canvas</span>
      </div>
    );
  }

  // Regular layer selected
  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Layer name badge */}
      <div style={{ padding: '10px 14px 8px', borderBottom: '1px solid #1e293b', flexShrink: 0 }}>
        <div style={{ fontSize: 11, fontWeight: 600, color: '#e2e8f0', marginBottom: 2 }}>{selectedLayer.label}</div>
        <div style={{ fontSize: 10, color: '#64748b', fontFamily: 'monospace' }}>{selectedLayer.type} · {selectedLayer.id}</div>
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', borderBottom: '1px solid #1e293b', flexShrink: 0 }}>
        {TABS.map((t) => (
          <button
            key={t.id}
            type="button"
            onClick={() => setTab(t.id)}
            style={{
              flex: 1, padding: '7px 0', fontSize: 10, fontWeight: 600, border: 'none', cursor: 'pointer',
              background: 'transparent',
              color: tab === t.id ? '#3b82f6' : '#64748b',
              borderBottom: `2px solid ${tab === t.id ? '#3b82f6' : 'transparent'}`,
              transition: 'color 0.15s, border-color 0.15s',
            }}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* Tab content */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '14px 14px', display: 'flex', flexDirection: 'column', gap: 0 }}>
        {tab === 'position' && <PositionTab layer={selectedLayer} />}
        {tab === 'appearance' && <AppearanceTab layer={selectedLayer} />}
        {tab === 'text' && <TextTab layer={selectedLayer} />}
        {tab === 'animation' && <AnimationTab layer={selectedLayer} />}
      </div>
    </div>
  );
}
